import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject, firstValueFrom } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { ProjectObject } from 'src/app/core/models/hvac-modes/project-object.model';
import { StoredFilter } from 'src/app/core/models/project/stored-filter.model';
import { ITemperatureDifferenceFilter, ITemperatureFilter } from 'src/app/core/models/view-filter.model';
import { CurrentUserStoreService } from 'src/app/core/services/current-user-store.service';
import { ProjectService } from 'src/app/modules/project/services/project.service';
import { LocationGroup } from './api-location-groups.service';
import { LocationTagsFilter, LocationsFilter } from 'src/app/core/models/locations-filter.model';
import { ApiProjectService } from 'src/app/modules/project/services/http/api-project.service';
import { DEMO_MODE } from 'src/environments/environment';
import { DemoModeService } from './demo-mode.service';
import { LocationGroupsService } from './location-groups.service';
import { Location } from 'src/app/core/models/project/location.model';
import { LocationGroupType } from 'src/app/core/models/location-group-type.enum';

export enum TemperatureFilterType {
  TemperatureFilter = 'TemperatureFilter',
  DaikinTemperatureFilter = 'DaikinTemperatureFilter',
  LgTemperatureFilter = 'LgTemperatureFilter',
  BoschTemperatureFilter = 'BoschTemperatureFilter',
  MitsubishiTemperatureFilter = 'MitsubishiTemperatureFilter',
  T3temperatureFilter = 'T3temperatureFilter',
  HaierTemperatureFilter = 'HaierTemperatureFilter',
  PanasonicTemperatureFilter = 'PanasonicTemperatureFilter',
}

export enum TemperatureDifferenceFilterType {
  TemperatureDifferenceFilter = 'TemperatureDifferenceFilter',
  DaikinTemperatureDifferenceFilter = 'DaikinTemperatureDifferenceFilter',
  LgTemperatureDifferenceFilter = 'LgTemperatureDifferenceFilter',
  BoschTemperatureDifferenceFilter = 'BoschTemperatureDifferenceFilter',
  MitsubishiTemperatureDifferenceFilter = 'MitsubishiTemperatureDifferenceFilter',
  T3temperatureDifferenceFilter = 'T3temperatureDifferenceFilter',
  HaierTemperatureDifferenceFilter = 'HaierTemperatureDifferenceFilter',
  PanasonicTemperatureDifferenceFilter = 'PanasonicTemperatureDifferenceFilter'

}

@Injectable({
  providedIn: 'root'
})

export class FiltersService {

  private searchFilter$ = new BehaviorSubject<string>('');
  private filters$ = new BehaviorSubject<LocationsFilter>(new LocationsFilter([1]));
  private commonAreaFilters$ = new BehaviorSubject<LocationsFilter>(new LocationsFilter([2,3,4]));
  loadedFiltersForUserId: number = undefined;
  loadedCommonAreaFiltersForUserId: number = undefined;

  constructor(
    private currentUserStoreService: CurrentUserStoreService,
    private apiProjectService: ApiProjectService,
    private projectService: ProjectService,
    private demoModeService: DemoModeService,
    private locationGroupsService: LocationGroupsService
  ) {}

  getFilters() {
    return this.filters$.asObservable();
  }

  setFilters(controllerFilters: LocationsFilter, refresh = true) {
    const activeUserId = this.currentUserStoreService.getUser().userId;
    const newControllerFilters = structuredClone(controllerFilters);
    StoredFilter.storeGuestRoomFilters(activeUserId, newControllerFilters)
    this.filters$.next(newControllerFilters);

    if (DEMO_MODE){
      this.demoModeService.setFilteredDemoProjectRooms(controllerFilters)
      return
    }
    if (refresh === true) {
      this.projectService.setFilteredGuestRoomLocations([]);
      this.projectService.setLoadingFilteredLocations(true)
      this.apiProjectService.getFilteredLocationsFat(newControllerFilters, 'guestRooms').subscribe(()=> {
        this.projectService.setLoadingFilteredLocations(false)
      });
    }
  }


  getCommonAreaFilters() {
    return this.commonAreaFilters$.asObservable();
  }

  setCommonAreaFilters(controllerFilters: LocationsFilter, refresh = true) {
    const activeUserId = this.currentUserStoreService.getUser().userId;
    const newControllerFilters = structuredClone(controllerFilters);
    StoredFilter.storeCommonAreaFilters(activeUserId, newControllerFilters)
    this.commonAreaFilters$.next(newControllerFilters);
    if (DEMO_MODE && refresh){
      this.demoModeService.setFilteredDemoProjectCommonAreas(controllerFilters)
      return
    }
    if (refresh === true) {
      this.projectService.setFilteredCommonAreasLocations([]);
      this.projectService.setLoadingFilteredLocations(true)
      this.apiProjectService.getFilteredLocationsFat(newControllerFilters, 'commonArea').subscribe(()=> {
        this.projectService.setLoadingFilteredLocations(false)
      });
    }

  }

  initFilters(controllerFilters: LocationsFilter) {
    const activeUserId = this.currentUserStoreService.getUser().userId;
    const newControllerFilters = structuredClone(controllerFilters);
    StoredFilter.storeGuestRoomFilters(activeUserId, newControllerFilters)
    this.filters$.next(newControllerFilters);
  }

  initCommonAreaFilters(controllerFilters: LocationsFilter) {
    const activeUserId = this.currentUserStoreService.getUser().userId;
    const newControllerFilters = structuredClone(controllerFilters);
    StoredFilter.storeCommonAreaFilters(activeUserId, newControllerFilters)
    this.commonAreaFilters$.next(newControllerFilters);
  }

  getSearchFilter() {
    return this.searchFilter$.asObservable();
  }

  setSearchFilter(newSearchFilter: string) {
    this.searchFilter$.next(newSearchFilter);
  }

  getObjectsFilter(): Observable<number[]> { // return object ids
    return this.filters$.pipe(map((f: LocationsFilter)=> {
      return f?.objects
    }))
  }

  getObjectsFilterFull(): Observable<ProjectObject[]> { // return full objects
    return this.projectService.getObjects().pipe(switchMap((objects: ProjectObject[])=> {
      return this.filters$.pipe(map((f: LocationsFilter)=> {
        return f?.objects?.map((objId)=> {
          return objects.find((obj: ProjectObject)=> {
            return obj.projectObjectId === objId;
          })
        })
      }))
    }))
  }

  setObjectsFilter(objectLocationIds: number[], refresh = true) {
    const newFilters = structuredClone(this.filters$.getValue());

    newFilters.objects = objectLocationIds
    newFilters.floors = [];
    this.setFilters(newFilters, refresh)
  }

  setCommonAreaObjectsFilter(objectLocationIds: number[], refresh = true) {
    // const newFilters =  JSON.parse(JSON.stringify(this.commonAreaFilters$.getValue()));
    const newFilters = structuredClone(this.commonAreaFilters$.getValue());

    newFilters.objects = objectLocationIds
    newFilters.floors = [];
    this.setCommonAreaFilters(newFilters, refresh)
  }

  getHvacSpeed(): Observable<null | 0 | 1 | 2 | 3> {
    return this.filters$.pipe(map((f: LocationsFilter)=> {
      return f.customControllerFilter.Hvac.HvacSpeed
    }))
  }


  // FLOOR FILTER

  getFloorsFilter(): Observable<number[]> {
    return this.filters$.pipe(map((f: LocationsFilter)=> {
      return f.floors
    }))
  }

  getFloorsFilterFull(): Observable<LocationGroup[]> { // return full floors
     return this.locationGroupsService.getFloorList().pipe(switchMap((allFloors: LocationGroup[])=> {
      return this.getFloorsFilter().pipe(map((floorInFilterIds: number[] )=> {
        return floorInFilterIds.map((floorId)=> {
          return allFloors.find((f: LocationGroup)=> {
            return f.id === floorId;
          })
        })
      }))
    }))
  }


  getTagsFilter(corridors: boolean): Observable<string[]> {
    if (corridors) {
      return this.commonAreaFilters$.pipe(map((f: LocationsFilter)=> {
        return f.locationTags?.locationTags
      }))
    } else {
      return this.filters$.pipe(map((f: LocationsFilter)=> {
        return f.locationTags?.locationTags
      }))
    }
  }

  changeFloorFilter(floor: LocationGroup) {
    const newFilters = structuredClone(this.filters$.getValue());

    const floorLocIdIndex = newFilters.floors.findIndex((flId: number) => {
      return flId === floor.id
    })
    if (floorLocIdIndex === -1) {
      newFilters.floors.push(floor.id)
    } else {
      newFilters.floors.splice(floorLocIdIndex,1)
    }
    this.setFilters(newFilters)
  }


  changeTagsFilters(tagName, corridors: boolean) {
    let newFilters
    let tagIndex;
    if (corridors) {
      newFilters = structuredClone(this.commonAreaFilters$.getValue());
    } else {
      newFilters = structuredClone(this.filters$.getValue());
    }
    if (newFilters.locationTags != null && newFilters.locationTags.locationTags != null) {
      tagIndex = newFilters.locationTags.locationTags.findIndex( tag => {
        return tag === tagName
      })
    } else {
      newFilters.locationTags = new LocationTagsFilter()
      newFilters.locationTags.locationTags = []
      newFilters.locationTags.locationTags.push(tagName)
    }

    if (tagIndex === -1) {
      newFilters.locationTags.locationTags.push(tagName)
    } else {
      newFilters.locationTags.locationTags.splice(tagIndex, 1)
    }
    if (corridors) {
      this.setCommonAreaFilters(newFilters)
    } else {
      this.setFilters(newFilters)
    }
  }

  selectAllFloors(refresh = true) {
    const newFilters = structuredClone(this.filters$.getValue());
    newFilters.floors = [];
    this.setFilters(newFilters, refresh)
  }

  clearTagsFilter(corridors: boolean) {
    let newFilters
    if (corridors) {
      newFilters = structuredClone(this.commonAreaFilters$.getValue());
    } else {
      newFilters = structuredClone(this.filters$.getValue());
    }
    newFilters.locationTags = new LocationTagsFilter()
    newFilters.locationTags.locationTags = []
    if (corridors) {
      this.setCommonAreaFilters(newFilters)
    } else {
      this.setFilters(newFilters)
    }
  }


  // COMMON AREA FLOOR FILTER
  getCommonAreaFloorsFilter(): Observable<number[]> {
    return this.commonAreaFilters$.pipe(map((f: LocationsFilter)=> {
      return f.floors
    }))
  }

  changeCommonAreaFloorFilter(floor: LocationGroup) {
    const newFilters = structuredClone(this.commonAreaFilters$.getValue());

    const floorLocIdIndex = newFilters.floors.findIndex((flId: number) => {
      return flId === floor.id
    })
    if (floorLocIdIndex === -1) {
      newFilters.floors.push(floor.id)
    } else {
      newFilters.floors.splice(floorLocIdIndex,1)
    }
    this.setCommonAreaFilters(newFilters)
  }

  selectAllCommonAreaFloors(refresh = true) {
    const newFilters = structuredClone(this.commonAreaFilters$.getValue());
    newFilters.floors = [];
    this.setCommonAreaFilters(newFilters, refresh)
  }


  // GROUP FILTER

  getGroupsFilter(): Observable<number[]> {
    return this.filters$.pipe(map((f: LocationsFilter)=> {
      return f.groups
    }))
  }

  changeGroupFilter(group: LocationGroup) {
    const newFilters = structuredClone(this.filters$.getValue());
    let groupLocIdIndex;
    if (newFilters.groups != null) {
      groupLocIdIndex =newFilters.groups.findIndex((grId: number) => {
        return grId === group.id
      })
    } else {
      newFilters.groups = []
      newFilters.groups.push(group.id)
    }
    if (groupLocIdIndex === -1) {
      newFilters.groups.push(group.id)
    } else {
      newFilters.groups.splice(groupLocIdIndex,1)
    }
    this.setFilters(newFilters)
  }

  selectAllGroups() {
    const newFilters = structuredClone(this.filters$.getValue());

    newFilters.groups = [];
    this.setFilters(newFilters)
  }

  groupingFilterIsActive(): Observable<boolean> {
    return this.filters$.pipe(map((filters)=> {
      return filters?.locationTags?.locationTags?.length >0 || filters?.floors?.length>0 || filters?.groups?.length>0;
    }))
  }

  statusFilterIsActive(): Observable<boolean> {
    return this.filters$.pipe(map((filters)=> {
      const cleaningFilterActive = Object.values(filters.customControllerFilter.Cleaning)?.some((f)=> {
        return f !== null;
      })
      if (cleaningFilterActive) {
        return true;
      }
      const hvacFilterActive = Object.values(filters.customControllerFilter.Hvac).some((f)=> {
        return f !== null;
      })
      if (hvacFilterActive) {
        return true;
      }
      const statusFilterActive = Object.values(filters.customControllerFilter.Statuses)?.some((f)=> {
        return f !== null;
      })
      if (statusFilterActive) {
        return true;
      }
      return false
    }))
  }



  commonAreaFloorFilterIsActive(): Observable<boolean> {
    return this.commonAreaFilters$.pipe(map((filters)=> {
      return filters?.floors?.length > 0;
    }))
  }

  commonAreaTagsFilterIsActive(): Observable<boolean> {
    return this.commonAreaFilters$.pipe(map((filters)=> {
      return filters?.locationTags?.locationTags?.length > 0;
    }))
  }

  locationTypeFilterIsActive(): Observable<boolean> {
    return this.commonAreaFilters$.pipe(map((filters)=> {
      return filters?.locationTypes.length < 3; // there are max 3 location types in common areas filter
    }))
  }



  ////////////// HVAC FILTER //////////////////
   getTempType(temperatureFilterType: TemperatureFilterType ) {
    return this.filters$.pipe(map((filters)=> {
      return filters.customControllerFilter.Hvac?.[temperatureFilterType]?.Type;
     }));
   }

   getComparisonOption(temperatureFilterType: TemperatureFilterType) {
    return this.filters$.pipe(map((filters)=> {
      return filters.customControllerFilter.Hvac?.[temperatureFilterType]?.Comparison;
     }));
   }

   getLocationOption(temperatureFilterType: TemperatureFilterType) {
    return this.filters$.pipe(map((filters)=> {
      return filters.customControllerFilter.Hvac?.[temperatureFilterType]?.Location
     }));
   }

   getTempFilter(temperatureFilterType: TemperatureFilterType) {
    return this.filters$.pipe(map((filters)=> {
      return filters.customControllerFilter.Hvac?.[temperatureFilterType]
     }));
   }


   getTemperature(temperatureFilterType: TemperatureFilterType) {
    return this.filters$.pipe(map((filters)=> {
     return filters.customControllerFilter.Hvac?.[temperatureFilterType]?.Value;
    }));
  }

   setOperatorOption(operatorOption: 0 | 1 | 2, temperatureFilterType: TemperatureFilterType) {
      const newFilters = structuredClone(this.filters$.getValue());

      newFilters.customControllerFilter.Hvac[temperatureFilterType].Comparison = operatorOption;
      this.setFilters(newFilters);
   }

  setTempFilter(newValue: null | ITemperatureFilter, temperatureFilterType: TemperatureFilterType) {
    const newFilters = structuredClone(this.filters$.getValue());

    newFilters.customControllerFilter.Hvac[temperatureFilterType] = newValue;
    this.setFilters(newFilters);
  }

   setTemperature(temperature: any, temperatureFilterType: TemperatureFilterType) {
    const newFilters = structuredClone(this.filters$.getValue());

    if (temperature === undefined || temperature === null || temperature === '') {
      newFilters.customControllerFilter.Hvac[temperatureFilterType].Value = null;
    } else {
      newFilters.customControllerFilter.Hvac[temperatureFilterType].Value = temperature;
    }
    this.setFilters(newFilters);
   }

  changeTempType(temperatureFilterType:TemperatureFilterType) {
    const currentTempOptionValue = this.filters$.getValue().customControllerFilter.Hvac[temperatureFilterType].Type
    if (temperatureFilterType == TemperatureFilterType.T3temperatureFilter) {
      // we use this for base set and current T3 temperature
      if (currentTempOptionValue === 2) {
        this.setTempType(3, temperatureFilterType);
      } else {
        this.setTempType(2, temperatureFilterType);
      }
    } else {
      if (currentTempOptionValue === 1) {
        this.setTempType(0, temperatureFilterType);
      } else {
        this.setTempType(1, temperatureFilterType);
      }
    }
   }

  setTempType(tempType: 0 | 1 | 2 | 3, temperatureFilterType:TemperatureFilterType) {
    const newFilters = structuredClone(this.filters$.getValue());

    newFilters.customControllerFilter.Hvac[temperatureFilterType].Type = tempType;
    this.setFilters(newFilters)
   }

  changeOperatorOption(temperatureFilterType:TemperatureFilterType) {
    const currentNumericalOperatorValue = this.filters$.getValue().customControllerFilter.Hvac?.[temperatureFilterType].Comparison;
    if (currentNumericalOperatorValue === 0) {
      this.setOperatorOption(1,temperatureFilterType);
    } else if (currentNumericalOperatorValue === 1) {
      this.setOperatorOption(2, temperatureFilterType);
    } else {
      this.setOperatorOption(0, temperatureFilterType);
    }
  }

  changeRoomOrBathroom(temperatureFilterType:TemperatureFilterType) {
    const currentLocationOption = this.filters$.getValue().customControllerFilter.Hvac?.[temperatureFilterType].Location
    if (currentLocationOption === 1) {
      this.setTempLocation(0, temperatureFilterType);
    } else {
      this.setTempLocation(1, temperatureFilterType);
    }
  }

  setTempLocation(newValue: 0 | 1, temperatureFilterType:TemperatureFilterType) {
   const newFilters = structuredClone(this.filters$.getValue());
   newFilters.customControllerFilter.Hvac[temperatureFilterType].Location = newValue;
   this.setFilters(newFilters);
  }

  ////////////////// Temperature difference filter //////////////////

  getTempDiffLocationOption(temperatureDifferenceFilterType: TemperatureDifferenceFilterType) {
    return this.filters$.pipe(map((filters)=> {
      return filters.customControllerFilter.Hvac?.[temperatureDifferenceFilterType]?.Location;
     }));
   }

   getTempDiffComparisonOption(temperatureDifferenceFilterType: TemperatureDifferenceFilterType) {
    return this.filters$.pipe(map((filters)=> {
      return filters.customControllerFilter.Hvac?.[temperatureDifferenceFilterType]?.Comparison;
     }));
   }

   getTempDiffFilter(temperatureDifferenceFilterType: TemperatureDifferenceFilterType) {
    return this.filters$.pipe(map((filters)=> {
     return filters.customControllerFilter.Hvac?.[temperatureDifferenceFilterType];
    }));
  }

   getTempDiffValue(temperatureDifferenceFilterType: TemperatureDifferenceFilterType) {
     return this.filters$.pipe(map((filters)=> {
      return filters.customControllerFilter.Hvac?.[temperatureDifferenceFilterType]?.Value;
     }));
   }

   setTempDiffFilter(newValue: null | ITemperatureDifferenceFilter, temperatureDifferenceFilterType: TemperatureDifferenceFilterType) {
    const newFilters = structuredClone(this.filters$.getValue());

    newFilters.customControllerFilter.Hvac[temperatureDifferenceFilterType] = newValue;
    this.setFilters(newFilters);
  }

   setTempDiffLocationOption(locationOption: 0 | 1, temperatureDifferenceFilterType: TemperatureDifferenceFilterType) {
    const newFilters = structuredClone(this.filters$.getValue());

    newFilters.customControllerFilter.Hvac[temperatureDifferenceFilterType].Location = locationOption;
    this.setFilters(newFilters);
   }

   setTempDiffValue(temperatureDiff: any, temperatureDifferenceFilterType: TemperatureDifferenceFilterType) {
    const newFilters = structuredClone(this.filters$.getValue());

    if (temperatureDiff === undefined || temperatureDiff === null || temperatureDiff === '') {
      newFilters.customControllerFilter.Hvac[temperatureDifferenceFilterType].Value = null;
    } else {
      newFilters.customControllerFilter.Hvac[temperatureDifferenceFilterType].Value = temperatureDiff;
    }
    this.setFilters(newFilters);
   }


  changeTempDiffRoomOrBathroom(temperatureDifferenceFilterType: TemperatureDifferenceFilterType) {
    const currentTempDiffLocationOption = this.filters$.getValue().customControllerFilter.Hvac[temperatureDifferenceFilterType].Location;
    if (currentTempDiffLocationOption === 1) {
      this.setTempDiffLocationOption(0, temperatureDifferenceFilterType);
    } else {
      this.setTempDiffLocationOption(1, temperatureDifferenceFilterType);
    }
  }

  changeTempDiffComparisonOption(temperatureDifferenceFilterType: TemperatureDifferenceFilterType) {
    const currentNumericalOperatorValue = this.filters$.getValue().customControllerFilter.Hvac[temperatureDifferenceFilterType].Comparison;
    if (currentNumericalOperatorValue === 0) {
      this.setTempDiffOperatorOption(1, temperatureDifferenceFilterType);
    } else if (currentNumericalOperatorValue === 1) {
      this.setTempDiffOperatorOption(2, temperatureDifferenceFilterType);
    } else {
      this.setTempDiffOperatorOption(0, temperatureDifferenceFilterType);
    }
  }

  setTempDiffOperatorOption(operatorOption: 0 | 1 | 2, temperatureDifferenceFilterType: TemperatureDifferenceFilterType) {
    const newFilters = structuredClone(this.filters$.getValue());

    newFilters.customControllerFilter.Hvac[temperatureDifferenceFilterType].Comparison = operatorOption;
    this.setFilters(newFilters);
 }
 //////////////////////////////////

 clearStatusFilters() {
  const newFilters: LocationsFilter = this.filters$.getValue();
  newFilters.customControllerFilter.Cleaning = {
    Inspected:  null,
      Cleaned:  null,
      HighestCleaningStatus: null,
      MakeUpRoom:  null
  };
  newFilters.customControllerFilter.Hvac = {
    Active:  null,
    Heating:  null,
    Cooling:  null,
    HvacSpeed: null,
    FancoilMode: null,
    FloorheatingRoom: null,
    FloorheatingBathroom: null,
    TemperatureFilter: null ,
    TemperatureDifferenceFilter: null,
    T3temperatureFilter: null,
    T3temperatureDifferenceFilter: null,
    DaikinTemperatureDifferenceFilter:null,
    DaikinTemperatureFilter: null,
    LgTemperatureDifferenceFilter:null,
    LgTemperatureFilter: null,
    BoschTemperatureDifferenceFilter:null,
    BoschTemperatureFilter: null,
    MitsubishiTemperatureDifferenceFilter:null,
    MitsubishiTemperatureFilter: null,
    HaierTemperatureDifferenceFilter: null,
    HaierTemperatureFilter: null,
    PanasonicTemperatureDifferenceFilter:null,
    PanasonicTemperatureFilter: null,

  };
  newFilters.customControllerFilter.Statuses = {
    Online: null,
    Rented: null,
    CardDeposited: null,
    AlarmActive: null,
    DoNotDisturb: null,
    RoomArmed: null,
    WindowOpen: null,
    DoorOpen: null,
    IgnoreWindow: null,
    IgnoreCardTray: null,
    MaidAtLocation: null,
    MiniBarOpen: null,
    MultipleControllers: null
    }

  this.setFilters(newFilters);
 }

  initializeFiltersFromStorage(userId: number) {
    if (this.loadedFiltersForUserId !== userId) { // check if filters are already loaded for this user
      const storedSettingsForUser = StoredFilter.getGuestRoomFilterFromStorage(userId);
      if (storedSettingsForUser) {
        this.initFilters(storedSettingsForUser.filters)
        this.loadedFiltersForUserId = userId;
      } else {
        this.initFilters(new LocationsFilter([1]));
      }
    }
  }

  initializeCommonAreaFiltersFromStorage(userId: number) {
    if (this.loadedCommonAreaFiltersForUserId !== userId) { // check if filters are already loaded for this user
      const storedSettingsForUser = StoredFilter.getCommonAreaFilterFromStorage(userId);
      if (storedSettingsForUser) {
        this.initCommonAreaFilters(storedSettingsForUser.filters)
        this.loadedFiltersForUserId = userId;
      } else {
        this.initCommonAreaFilters(new LocationsFilter([2,3,4]));
      }
    }
  }

  updateCleaningFilterStatus(statusName: string, newStatus: any | null ) {
    const newFilters = structuredClone(this.filters$.getValue());

    newFilters.customControllerFilter.Cleaning[statusName] = newStatus;
    this.setFilters(newFilters)
  }

  updateOtherFilterStatus(statusName: string, newStatus: boolean | null ) {
    const newFilters = structuredClone(this.filters$.getValue());

    newFilters.customControllerFilter.Statuses[statusName] = newStatus;
    this.setFilters(newFilters)
  }

  updateHvacFilterStatus(statusName: string, newStatus: boolean | null ) {
    const newFilters = structuredClone(this.filters$.getValue());

    newFilters.customControllerFilter.Hvac[statusName] = newStatus;
    this.setFilters(newFilters)
  }

  // COMMON AREA FILTER
  isControllerTypeFilterActive(controllerTypeFilter: 2 | 3 | 4) {
    const contTypesFilter = this.commonAreaFilters$.getValue().locationTypes;
    return contTypesFilter.includes(controllerTypeFilter);
  }

  onControllerTypeFilterClick(controllerTypeFilter: 2 | 3 | 4) {
    const newFilters = structuredClone(this.commonAreaFilters$.getValue());

    const typeIndex = newFilters.locationTypes.findIndex((type: number) => {
      return controllerTypeFilter === type
    })
    if (typeIndex === -1) {
      newFilters.locationTypes.push(controllerTypeFilter)
    } else {
      newFilters.locationTypes.splice(typeIndex,1)
    };

    this.setCommonAreaFilters(newFilters)
  }


  resetCommonAreaControllerTypeFilters() {
    const newFilters = structuredClone(this.commonAreaFilters$.getValue());

    newFilters.locationTypes = [2,3,4];
    this.setCommonAreaFilters(newFilters);
  }

  async cachePartialGuestRoomLocations() {
    const filteredGuestRoomLocations = await firstValueFrom(this.projectService.getFilteredGuestRoomLocations());
    const floors: LocationGroup[] = await firstValueFrom(this.locationGroupsService.getFloorList());
    const floorFiltersIds: number[] = this.filters$.getValue().floors;

    let floorsToCache: LocationGroup[];
    if (floorFiltersIds.length >0 ) {
      floorsToCache = floors.filter((fl)=> {
        return floorFiltersIds.includes(fl.id)
      })
    } else {
      floorsToCache =  floors;
      const undistributedLocations = filteredGuestRoomLocations.filter((loc)=> {
        return !floors.some((floor)=> {
          return floor.locations.some((l)=> {
            return loc.locationId === l.locationId
          })
        })
      })
      const undistributedFloor: LocationGroup =
        { id: 9999,
          locationGroupTypeId: LocationGroupType.FloorGroup,
          locations: undistributedLocations,
          name: 'Undistributed'
        };
      floorsToCache.push(undistributedFloor);
    }

    let locationsToCache = [];
    floorsToCache.forEach((floor: LocationGroup) => {
      const locIdsInFloor: number[] = floor.locations.map((l: Location)=> {
        return l.locationId;
      })
      const sortedLocsOnFloor = filteredGuestRoomLocations.filter((loc)=> {
        return locIdsInFloor.includes(loc.locationId)
      })
      locationsToCache = [...locationsToCache,...sortedLocsOnFloor]
    });

    // triger loading spiner when comming to room overview if more then 50 locations
    if (locationsToCache.length>50) {
      this.projectService.guestRoomLocationsCachedAndSizeReduced$.next(true);
    } else {
      this.projectService.guestRoomLocationsCachedAndSizeReduced$.next(false);
    }

    this.projectService.setFilteredGuestRoomLocations(locationsToCache.slice(0,50));
  }
}
